import { useEffect, useRef, useState } from 'react';

import {
  TRELLO_JIRA_CO_USERS_VNEXT,
  useUserTrait,
} from '@trello/atlassian-personalization';
import { getAaId, getMemberIdWithFallback } from '@trello/authentication';
import { useUserAge } from '@trello/business-logic-react/member';

import { getAccessibleProducts } from '../../getAccessibleProducts';
import type { RuntimeEligibilityCheckResult } from './RuntimeEligibilityCheck';
import { useIsCrossFlowMemberConfirmed } from './useIsCrossFlowMemberConfirmed';

export const useHasNoAvailableJiraSites = ({
  doLoadAvailableSites,
}: {
  doLoadAvailableSites: boolean;
}): RuntimeEligibilityCheckResult => {
  const isLoadingAvailableSites = useRef(false);

  const [hasNoAvailableSites, setHasNoAvailableSites] = useState(false);

  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();
  const userAge = useUserAge();
  const memberId = getMemberIdWithFallback();
  const aaId = getAaId();
  const isNewUser = userAge < 2;

  const { value: isJiraCoUserTrait, loading: isJiraCoUserTraitLoading } =
    useUserTrait(TRELLO_JIRA_CO_USERS_VNEXT);

  useEffect(() => {
    const loadSiteEligibility = async (): Promise<void> => {
      try {
        isLoadingAvailableSites.current = true;
        const { products } = await getAccessibleProducts();
        const hasNoSites =
          products?.length > 0 &&
          products?.filter(({ productId }) => productId !== 'trello').length ===
            0;
        setHasNoAvailableSites(hasNoSites);
      } finally {
        isLoadingAvailableSites.current = false;
      }
    };

    // if the user isn't confirmed, they won't have any sites
    if (!isMemberConfirmed) {
      // TODO ENGHEALTH-2180: baselined when the rule was enabled
      setHasNoAvailableSites(true); // oxlint-disable-line react/set-state-in-effect
      return;
    }

    if (!isNewUser) {
      if (!aaId) {
        setHasNoAvailableSites(true);
      } else if (!isJiraCoUserTraitLoading) {
        setHasNoAvailableSites(!isJiraCoUserTrait);
      }
    } else {
      if (doLoadAvailableSites && !isLoadingAvailableSites.current) {
        loadSiteEligibility();
      }
    }
  }, [
    doLoadAvailableSites,
    isMemberConfirmed,
    isNewUser,
    aaId,
    memberId,
    isJiraCoUserTrait,
    isJiraCoUserTraitLoading,
  ]);

  return {
    isEligible: hasNoAvailableSites,
  };
};
